import React, { useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined"
import { Grid } from "@mui/material"
import { Button } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { ValueBlock } from "../../../../components/data-display/ValueBlock"
import { getTheme } from "../../../../hooks/UseTheme"
import { KonanAPI } from "../../../../services/KonanAPI"
import type { PaginatedScoreCardSetListList } from "../../../../types/generated/api/PaginatedScoreCardSetListList"
import type { ScoreCardSetList } from "../../../../types/generated/api/ScoreCardSetList"
import { useNodeDeletion } from "../../hooks/useNodeHooks"
import type { ScorecardsetNodeProps, SelectionDataItemType } from "../../types/interfaces"
import { nodeHelpers } from "../../utils/nodeHelpers"
import { SelectDataMenu } from "./SelectDataMenu"
import { BaseWorkflowNode } from "./base/BaseWorkflowNode"

/**
 * ScorecardsetNode component handles scorecard set selection and configuration in workflows.
 * Supports scorecard set selection and external navigation.
 */
export function ScorecardsetNode(props: Readonly<ScorecardsetNodeProps>): React.ReactElement {
  const { data, menuView, id } = props
  const updateGraph = props.data?.updateGraph
  const scorecardsets = data?.scorecardsets
  const readMode: boolean | null = data?.readMode ?? null
  const nodeId = id ?? null
  const currentDataItemId = data?.scorecard_set

  // Navigation hooks
  const { id: projectId } = useParams<{ id: string }>()

  // Theme hook
  const theme = getTheme()

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "selectionNode",
  })

  // Constants
  const page = 1
  const pageSize = 200

  // Get node metadata
  const metadata = nodeHelpers.getNodeMetadata("ScorecardsetNode")

  // Use our custom hook for node deletion
  const { handleRemoveNode } = useNodeDeletion(nodeId as string, updateGraph)

  // Data fetching queries
  const { isLoading: isScorecardsetsLoading, data: scorecardsetsData } = useQuery<
    AxiosResponse<PaginatedScoreCardSetListList>,
    AxiosError
  >(
    ["scorecardsets", projectId, page, pageSize],
    () => KonanAPI.fetchScorecardsets({ projectId: projectId as string, page, pageSize }),
    {
      enabled: !!projectId && !menuView && !readMode && data?.nodeType === "ScorecardsetNode",
    },
  )

  const currentData = (): PaginatedScoreCardSetListList | undefined => {
    return scorecardsetsData?.data ?? scorecardsets
  }

  const [selectedValue, setSelectedValue] = useState<SelectionDataItemType | undefined | string>(
    currentDataItemId
      ? typeof currentDataItemId === "string"
        ? currentDataItemId
        : (currentDataItemId as ScoreCardSetList)?.uuid
      : undefined,
  )

  return (
    <BaseWorkflowNode
      id={nodeId as string}
      menuView={menuView}
      readMode={readMode ?? undefined}
      nodeType="ScorecardsetNode"
      title={t("nodeTitles.scorecardset")}
      iconProps={{
        element: <AssignmentOutlinedIcon sx={{ color: theme.palette.base.text[1] }} />,
        className: metadata.iconClassName,
      }}
      width={metadata.width}
      handlesProps={{ showTarget: metadata.showTargetHandle, showSource: metadata.showSourceHandle }}
      onRemove={() => handleRemoveNode(t("notifications.nodeRemoved", { nodeType: "scorecardset" }))}
    >
      {/* Node Content */}
      <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
        {/* Data Selection */}
        <Grid item xs={10}>
          {!menuView && !readMode ? (
            <SelectDataMenu
              nodeId={nodeId}
              dataType="ScorecardsetNode"
              dataItemId={currentDataItemId}
              menuView={menuView}
              itemData={currentData()}
              selectedValue={selectedValue as string}
              setSelectedValue={setSelectedValue}
              isDataLoading={isScorecardsetsLoading}
            />
          ) : (
            <Grid container justifyContent="space-between" alignItems="center">
              <ValueBlock
                value={
                  typeof currentDataItemId === "string"
                    ? currentDataItemId
                    : (currentDataItemId as ScoreCardSetList)?.name || t("nodeBodies.noScorecardsetSelected")
                }
                size={12}
                showTooltip={!menuView}
              />
            </Grid>
          )}
        </Grid>

        {/* Action Button - External Link */}
        <Grid item xs={2} ml={0.5}>
          <Button
            onClick={() =>
              setTimeout(() => {
                window.open(`/projects/${projectId}/Scorecardsets`, "_blank")
              })
            }
            disabled={menuView}
            size={"regular"}
            fullWidth
            data-mode={readMode ? "read" : menuView ? "edit-view" : "edit"}
            variant="secondary"
          >
            <LaunchOutlinedIcon fontSize="small" sx={{ color: theme.palette.base.text[1] }} />
          </Button>
        </Grid>
      </Grid>
    </BaseWorkflowNode>
  )
}
